/**
 * deadImages — Registre GLOBAL des images définitivement mortes.
 *
 * Une image est « morte » quand TOUS les proxies/essais de chargement ont
 * échoué (ex. produit Coutr dont l'image Shopify a été supprimée → 404 partout).
 * Le filtre client (URL non vide) ne peut pas détecter un 404 : seul un échec
 * de chargement réel le révèle. Ce registre mémorise ces URLs pour la session.
 *
 * But : « pas d'image = pas affiché » étendu aux LIENS MORTS partenaires, et
 * appliqué PARTOUT de façon cohérente. Dès qu'une image est marquée morte :
 *   - catalogue    → la tuile est retirée (via useDeadImagesVersion + filtre),
 *   - styliste     → l'outfit qui la contient est droppé (hasUsableImage → false),
 *   - looks/calendrier → idem via hasUsableImage.
 *
 * On ne marque mort QUE depuis les composants à proxies (SmartImage), qui
 * épuisent plusieurs relais avant de conclure → évite les faux positifs sur un
 * simple aléa réseau transitoire.
 *
 * v1.13.43
 */
import { useState, useEffect } from 'react';

const DEAD = new Set();
const listeners = new Set();

// Normalise pour matcher quelle que soit la casse http/https.
function norm(u) {
  if (!u || typeof u !== 'string') return '';
  let s = u.trim();
  if (s.startsWith('http://')) s = 'https://' + s.slice(7);
  return s;
}

export function markImageDead(uri) {
  const k = norm(uri);
  if (!k || DEAD.has(k)) return;
  DEAD.add(k);
  listeners.forEach((fn) => { try { fn(); } catch {} });
}

export function isImageDead(uri) {
  const k = norm(uri);
  return !!k && DEAD.has(k);
}

// Un item catalogue/look a-t-il une image morte ? (couvre les noms de champ usuels)
export function isItemImageDead(item) {
  if (!item) return false;
  const u = typeof item === 'string'
    ? item
    : (item.image || item.image_url || item.photoUrl || item.imageUrl || '');
  return isImageDead(u);
}

export function subscribeDeadImages(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// Hook React : force un re-render du composant quand le registre grandit,
// pour que les listes (catalogue, styliste) se recalculent et retirent l'item.
export function useDeadImagesVersion() {
  const [v, setV] = useState(0);
  useEffect(() => subscribeDeadImages(() => setV((x) => x + 1)), []);
  return v;
}
